import { Helmet } from "react-helmet-async";

const PrivacyPolicy = () => {
  return (
    <div className="bg-slate-50 min-h-screen">
      <Helmet>
        <title>My Church Directory Privacy Policy | Online Church Directory Software</title>

        <meta
          name="description"
          content="Read the Mycdir.com Privacy Policy to learn how we collect, use, safeguard, and protect church directory and member information across our online church management platform and mobile application."
        />
      </Helmet>

      <section className="bg-white">
        <div className="max-w-6xl mx-auto px-6 py-20 text-center">
          <span className="inline-flex rounded-full bg-sky-100 text-sky-700 px-4 py-1 text-sm font-medium">
            Privacy Policy
          </span>

          <h1 className="different_heading mt-5 text-4xl lg:text-5xl font-bold text-slate-900">
            Your Privacy Matters
          </h1>

          <p className="mt-6 max-w-4xl mx-auto text-lg text-slate-600 leading-8">
            At Mycdir.com (operated by Data Repo LLC, referred to as
            "Mycdir.com," "we," "our," or "us"), we respect the privacy of
            churches, church administrators, and individual church members.
            This Privacy Policy explains how we collect, use, safeguard, and
            disclose personal information when you visit our website, use our
            Online Church Management Directory platform, or access the My Church
            Directory mobile application (collectively, the "Services").
          </p>

          <div className="mt-6 text-sm text-slate-500">
            <p>
              <strong>Effective Date:</strong> August 10, 2026
            </p>
            <p className="mt-1">
              <strong>Last Updated:</strong> August 10, 2026
            </p>
          </div>
        </div>
      </section>

      <section className="py-20">
        <div className="max-w-5xl mx-auto px-6 space-y-6">
          <div className="rounded-2xl bg-white border border-slate-200 shadow-sm p-8">
            <div className="flex items-center gap-4 mb-4">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-secondary text-white font-semibold">
                1
              </div>

              <h2 className="text-2xl font-bold text-slate-900">
                Information We Collect
              </h2>
            </div>

            <p className="text-slate-600 leading-8 mb-6">
              We collect information in three ways: directly from church
              administrators and members, automatically through platform
              use, and via member-submitted profile updates.
            </p>

            <h3 className="text-xl font-semibold text-slate-900 mb-3">
              A. Information Provided by Church Administrators & Members
            </h3>

            <ul className="list-disc pl-6 text-slate-600 leading-8 space-y-2">
              <li>
                <strong>Church Administrator Account Data:</strong>{" "}
                Name, ministry/staff role, official church email
                address, phone number, billing address, and
                subscription payment details (processed securely via
                third-party payment gateways).
              </li>

              <li>
                <strong>Directory Profile Information:</strong>{" "}
                Member first and last names, family unit relationships,
                profile photos, phone numbers, email addresses,
                physical street addresses, birth dates, and
                anniversary dates.
              </li>

              <li>
                <strong>Communication Content:</strong>{" "}
                Emails and SMS text message content created by church
                leaders to send to members or specific ministry groups.
              </li>

              <li>
                <strong>Member Portal Submissions:</strong>{" "}
                Prayer requests, event registration details, group
                assignments, and voluntary profile edits submitted by
                members for administrator approval.
              </li>
            </ul>

            <h3 className="text-xl font-semibold text-slate-900 mt-8 mb-3">
              B. Information Collected Automatically
            </h3>

            <ul className="list-disc pl-6 text-slate-600 leading-8 space-y-2">
              <li>
                <strong>Device & Connection Data:</strong>{" "}
                IP address, operating system, browser type, device
                identifier, app version, and login timestamp logs.
              </li>

              <li>
                <strong>Usage & Analytics:</strong>{" "}
                App performance data, crash reports, feature
                engagement, and navigation paths inside the
                administrative portal.
              </li>
            </ul>
          </div>

          <div className="rounded-2xl bg-white border border-slate-200 shadow-sm p-8">
            <div className="flex items-center gap-4 mb-4">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-secondary text-white font-semibold">
                2
              </div>

              <h2 className="text-2xl font-bold text-slate-900">
                How We Use Your Information
              </h2>
            </div>

            <p className="text-slate-600 leading-8 mb-6">
              We strictly use the collected data to provide, maintain,
              and secure the Mycdir.com platform. Specific processing
              purposes include:
            </p>

            <ul className="list-disc pl-6 text-slate-600 leading-8 space-y-2">
              <li>
                <strong>Directory Services:</strong>{" "}
                Enabling authorized church members to search, locate,
                and contact fellow congregation members via call,
                email, or mapping directions.
              </li>

              <li>
                <strong>Church Communications:</strong>{" "}
                Facilitating email broadcasts and optional SMS text
                alert delivery from church leaders to members or
                specific groups.
              </li>

              <li>
                <strong>Member Synchronization:</strong>{" "}
                Instantly syncing administrator-approved updates
                across authorized mobile devices (including offline
                directory storage).
              </li>

              <li>
                <strong>Platform Security:</strong>{" "}
                Authenticating valid church member logins, preventing
                unauthorized public access, and maintaining database
                integrity.
              </li>

              <li>
                <strong>Billing & Support:</strong>{" "}
                Managing church subscription plans, processing text
                credit purchases, and responding to technical support
                inquiries.
              </li>
            </ul>
          </div>

          <div className="rounded-2xl bg-white border border-slate-200 shadow-sm p-8">
            <div className="flex items-center gap-4 mb-4">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-secondary text-white font-semibold">
                3
              </div>

              <h2 className="text-2xl font-bold text-slate-900">
                Strict Data Privacy Guarantee (No Selling or Commercial Access)
              </h2>
            </div>

            <p className="text-slate-600 leading-8 mb-6">
              We do not sell, rent, trade, or monetize church directory
              data or member personal information under any circumstances.
            </p>

            <ul className="list-disc pl-6 text-slate-600 leading-8 space-y-2">
              <li>
                <strong>No Internal Marketing Use:</strong>{" "}
                We do not access member directory data to market
                third-party services to church members or their families.
              </li>

              <li>
                <strong>Troubleshooting Only:</strong>{" "}
                Mycdir.com staff only access directory data when
                explicitly necessary for backend technical
                troubleshooting or system maintenance requested by the
                church.
              </li>

              <li>
                <strong>No Public Indexing:</strong>{" "}
                Member directories are protected behind secure
                authentication and are strictly closed from public web
                search engines.
              </li>
            </ul>
          </div>

          <div className="rounded-2xl bg-white border border-slate-200 shadow-sm p-8">
            <div className="flex items-center gap-4 mb-4">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-secondary text-white font-semibold">
                4
              </div>

              <h2 className="text-2xl font-bold text-slate-900">
                How Information Is Shared
              </h2>
            </div>

            <p className="text-slate-600 leading-8 mb-6">
              We share personal data only with trusted third parties
              strictly necessary to deliver core platform services:
            </p>

            <ul className="list-disc pl-6 text-slate-600 leading-8 space-y-2">
              <li>
                <strong>Service Providers:</strong>{" "}
                Infrastructure and communications vendors, including
                cloud hosting providers, database servers, and
                commercial SMS gateway aggregators used for delivering
                text alerts.
              </li>

              <li>
                <strong>Church Administrators:</strong>{" "}
                Designated editors and administrators of your specific
                church who hold primary control over member profile
                approvals, group assignments, and directory permissions.
              </li>

              <li>
                <strong>Legal Requirements:</strong>{" "}
                When required by applicable law, subpoena, court order,
                or governmental regulation to protect legal rights or
                ensure user safety.
              </li>
            </ul>
          </div>

          <div className="rounded-2xl bg-white border border-slate-200 shadow-sm p-8">
            <div className="flex items-center gap-4 mb-4">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-secondary text-white font-semibold">
                5
              </div>

              <h2 className="text-2xl font-bold text-slate-900">
                Security and Access Controls
              </h2>
            </div>

            <p className="text-slate-600 leading-8 mb-6">
              We employ multi-layered technical and organizational
              security measures to protect church directory records:
            </p>

            <ul className="list-disc pl-6 text-slate-600 leading-8 space-y-2">
              <li>
                <strong>Authenticated Access:</strong>{" "}
                Only authorized church members with a validated email
                address listed in the church's directory can set up
                credentials and gain access.
              </li>

              <li>
                <strong>Encryption:</strong>{" "}
                Data transmitted between member devices, mobile apps,
                and servers is encrypted using industry-standard
                security protocols.
              </li>

              <li>
                <strong>Instant Revocation:</strong>{" "}
                If a church administrator revokes a member's directory
                access, the directory database is automatically wiped
                from that member's device upon the next sync.
              </li>

              <li>
                <strong>Privacy Controls:</strong>{" "}
                Members and administrators can customize individual
                profile visibility (e.g., opting to hide specific
                phone numbers or street addresses from general member
                view).
              </li>
            </ul>
          </div>

          <div className="rounded-2xl bg-white border border-slate-200 shadow-sm p-8">
            <div className="flex items-center gap-4 mb-4">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-secondary text-white font-semibold">
                6
              </div>

              <h2 className="text-2xl font-bold text-slate-900">
                Communication Compliance (Email & Text Messaging)
              </h2>
            </div>

            <ul className="list-disc pl-6 text-slate-600 leading-8 space-y-2">
              <li>
                <strong>Email Broadcasts:</strong>{" "}
                Emails sent through Mycdir.com are handled in compliance
                with CAN-SPAM regulations, delivering clear header
                information and church branding without third-party
                advertisements.
              </li>

              <li>
                <strong>SMS Messaging:</strong>{" "}
                Church text alerts use dedicated commercial short codes.
                Message frequency varies based on church activity.
                Members can opt out of SMS communications at any time
                by following the reply prompt (e.g., replying STOP) or
                contacting their church administrator.
              </li>
            </ul>
          </div>

          <div className="rounded-2xl bg-white border border-slate-200 shadow-sm p-8">
            <div className="flex items-center gap-4 mb-4">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-secondary text-white font-semibold">
                7
              </div>

              <h2 className="text-2xl font-bold text-slate-900">
                Data Retention & Deletion Rights
              </h2>
            </div>

            <ul className="list-disc pl-6 text-slate-600 leading-8 space-y-2">
              <li>
                <strong>Retention Period:</strong>{" "}
                Member records are retained for as long as the church
                maintains an active subscription with Mycdir.com or
                until an administrator removes the specific member
                record.
              </li>

              <li>
                <strong>Account Deletion & Removal:</strong>{" "}
                Church members who wish to have their profile removed
                from their church directory can submit a request
                directly to their church administrator or contact
                Mycdir.com customer support. Upon subscription
                cancellation by a church, associated database records
                are queued for complete system deletion.
              </li>
            </ul>
          </div>

          <div className="rounded-2xl bg-white border border-slate-200 shadow-sm p-8">
            <div className="flex items-center gap-4 mb-4">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-secondary text-white font-semibold">
                8
              </div>

              <h2 className="text-2xl font-bold text-slate-900">
                Children's Privacy
              </h2>
            </div>

            <p className="text-slate-600 leading-8">
              Mycdir.com provides church management software designed for
              family directory organization. Minor family members
              (children) may be listed under a parent or guardian's
              family profile by church administrators. We do not knowingly
              collect personal information directly from children under
              13 without verifiable parental/guardian consent managed by
              the church administration.
            </p>
          </div>

          <div className="rounded-2xl bg-white border border-slate-200 shadow-sm p-8">
            <div className="flex items-center gap-4 mb-4">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-secondary text-white font-semibold">
                9
              </div>

              <h2 className="text-2xl font-bold text-slate-900">
                Updates to This Policy
              </h2>
            </div>

            <p className="text-slate-600 leading-8">
              We may update this Privacy Policy periodically to reflect
              enhancements to our church management tools or updates to
              legal requirements. When changes are made, we will update
              the "Last Updated" date at the top of this policy.
            </p>
          </div>

          <div className="rounded-2xl bg-white border border-slate-200 shadow-sm p-8">
            <div className="flex items-center gap-4 mb-4">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-secondary text-white font-semibold">
                10
              </div>

              <h2 className="text-2xl font-bold text-slate-900">
                Contact Us
              </h2>
            </div>

            <p className="text-slate-600 leading-8 mb-4">
              If you have questions or concerns regarding this Privacy
              Policy or data handling practices at Mycdir.com, please
              contact us at:
            </p>

            <p className="text-slate-600 leading-8">
              <strong>Website:</strong>{" "}
              <a
                href="https://mycdir.com/contact-us"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sky-600 hover:text-sky-700 underline"
              >
                https://mycdir.com/contact-us
              </a>
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PrivacyPolicy;